import { Camera, Eye, EyeOff, Pencil, Save, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { SNAPSHOT_DESCRIPTION_MAX_LENGTH } from './constants';
import { SnapshotAnnotationWorkspace } from './snapshot-annotation-workspace';
import type { SnapshotAnnotationWorkspaceHandle } from './snapshot-annotation-workspace';
import { SnapshotCaptureWizard } from './snapshot-capture-wizard';
import { SnapshotWindow } from './snapshot-window';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { annotationsEqual, bakeAnnotations } from '@/components/snapshot/annotation';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useViewportSnapshot } from '@/hooks/use-viewport-snapshot';
import { cn } from '@/lib/utils';
import { ViewMode, getSnapshotDisplayUrl } from '@/visualization';
import type { SnapshotBaseVariant, ViewportSnapshot } from '@/visualization';

// ============================================================================
// Constants
// ============================================================================

const VIEW_MODE_LABELS: Record<ViewMode, string> = {
    [ViewMode.VIEW_3D]: '3D',
    [ViewMode.VIEW_2D_AXIAL]: 'Axial',
    [ViewMode.VIEW_2D_SAGITTAL]: 'Sagittal',
    [ViewMode.VIEW_2D_CORONAL]: 'Coronal',
    [ViewMode.VIEW_2D_OBLIQUE]: 'Oblique',
    [ViewMode.VIEW_CPR_STRETCHED]: 'Stretched CPR',
    [ViewMode.VIEW_CPR_STRAIGHTENED]: 'Straightened CPR',
};

// ============================================================================
// SnapshotThumbnail
// ============================================================================

interface SnapshotThumbnailProps {
    snapshot: ViewportSnapshot;
    isSelected: boolean;
    onSelect: () => void;
    /** Omitted for template captures, which cannot be deleted (see SnapshotPanel). */
    onRemove?: () => void;
    onToggleReport: () => void;
}

function SnapshotThumbnail({
    snapshot,
    isSelected,
    onSelect,
    onRemove,
    onToggleReport,
}: SnapshotThumbnailProps) {
    const ReportIcon = snapshot.includedInReport ? Eye : EyeOff;

    return (
        <div
            className={`group relative cursor-pointer rounded-md overflow-hidden border-2 transition-colors ${
                isSelected ? 'border-primary' : 'border-transparent hover:border-border'
            } ${!snapshot.includedInReport ? 'opacity-40' : ''}`}
            onClick={onSelect}
        >
            <img
                src={getSnapshotDisplayUrl(snapshot)}
                alt={snapshot.label ?? VIEW_MODE_LABELS[snapshot.viewMode]}
                className="h-24 w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-background/80 px-1.5 py-0.5">
                <p className="truncate text-xs leading-tight">
                    {snapshot.label ?? VIEW_MODE_LABELS[snapshot.viewMode]}
                </p>
            </div>
            <button
                className="absolute left-0.5 top-0.5 hidden rounded-sm bg-background/80 p-0.5 group-hover:flex"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleReport();
                }}
                aria-label={snapshot.includedInReport ? 'Exclude from report' : 'Include in report'}
            >
                <ReportIcon className="h-2.5 w-2.5" />
            </button>
            {onRemove && (
                <button
                    className="absolute right-0.5 top-0.5 hidden rounded-sm bg-background/80 p-0.5 group-hover:flex"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    aria-label="Delete"
                >
                    <X className="h-2.5 w-2.5" />
                </button>
            )}
        </div>
    );
}

// ============================================================================
// SnapshotPanel
// ============================================================================

interface SnapshotPanelProps {
    onClose: () => void;
}

/**
 * Screenshots gallery in the shared fixed window. Annotating a saved snapshot
 * switches the window content to an edit view (same window, same dimensions)
 * instead of opening a second window.
 */
function SnapshotPanel({ onClose }: SnapshotPanelProps) {
    const {
        snapshots,
        removeSnapshot,
        toggleReportInclusion,
        updateSnapshotDescription,
        updateSnapshotAnnotations,
    } = useViewportSnapshot();
    const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
    const [editingDescription, setEditingDescription] = useState<string>('');
    const [isRebaking, setIsRebaking] = useState(false);

    // Saved snapshot being annotated (in-window edit mode).
    const [editingSnapshotId, setEditingSnapshotId] = useState<string | null>(null);
    const [editVariant, setEditVariant] = useState<SnapshotBaseVariant>('overlay');
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
    const workspaceRef = useRef<SnapshotAnnotationWorkspaceHandle | null>(null);

    // Auto-select the latest snapshot when a new one is added
    useEffect(() => {
        if (snapshots.length === 0) {
            setSelectedSnapshotId(null);
            return;
        }
        const latest = snapshots[snapshots.length - 1];
        if (!selectedSnapshotId || !snapshots.find((s) => s.id === selectedSnapshotId)) {
            setSelectedSnapshotId(latest.id);
        }
    }, [snapshots, selectedSnapshotId]);

    const selectedSnapshot = snapshots.find((s) => s.id === selectedSnapshotId) ?? null;
    const editingSnapshot = snapshots.find((s) => s.id === editingSnapshotId) ?? null;

    // Sync description only when the selected snapshot changes
    useEffect(() => {
        setEditingDescription(selectedSnapshot?.description ?? '');
    }, [selectedSnapshotId]); // intentionally excludes selectedSnapshot to avoid reset mid-typing

    // Template captures are regenerated from the report template on every capture
    // pass, so deleting one cannot be honoured — it would come back on the next
    // pass (and a dangling id used to duplicate the whole set). Use the report
    // toggle (`includedInReport`) to leave one out of the report instead.
    const handleRemove = (id: string) => {
        const target = snapshots.find((s) => s.id === id);
        if (!target || target.origin === 'template') return;

        removeSnapshot(id);
        if (selectedSnapshotId === id) {
            const remaining = snapshots.filter((s) => s.id !== id);
            setSelectedSnapshotId(remaining.length > 0 ? remaining[remaining.length - 1].id : null);
        }
    };

    const handleDescriptionBlur = () => {
        if (selectedSnapshot) {
            updateSnapshotDescription(selectedSnapshot.id, editingDescription.trim());
        }
    };

    // Switch the snapshot's base between the overlay/no-overlay native captures.
    // If the snapshot has annotations, rebake the composite on the new base.
    const handleToggleOverlay = async (snapshot: ViewportSnapshot) => {
        if (!snapshot.dataUrlNoOverlay) return;

        const nextVariant: SnapshotBaseVariant =
            snapshot.baseVariant === 'overlay' ? 'no-overlay' : 'overlay';

        if (snapshot.annotations.length === 0) {
            updateSnapshotAnnotations(snapshot.id, [], null, nextVariant);
            return;
        }

        setIsRebaking(true);
        try {
            const nextBaseUrl = nextVariant === 'overlay' ? snapshot.dataUrl : snapshot.dataUrlNoOverlay;
            const baked = await bakeAnnotations(nextBaseUrl, snapshot.annotations);
            updateSnapshotAnnotations(snapshot.id, snapshot.annotations, baked, nextVariant);
        } finally {
            setIsRebaking(false);
        }
    };

    const startEditing = (snapshot: ViewportSnapshot) => {
        setEditVariant(snapshot.baseVariant);
        setEditingSnapshotId(snapshot.id);
    };

    const stopEditing = () => {
        setConfirmDiscardOpen(false);
        setEditingSnapshotId(null);
    };

    // Escape/X/Back from the edit view: leave immediately when nothing changed,
    // otherwise ask for confirmation before dropping the unsaved annotations.
    const requestStopEditing = () => {
        if (!editingSnapshot) return;
        // getAnnotations() force-commits any open in-place text edit so the
        // dirty check sees the text the user actually typed.
        const latest = workspaceRef.current?.getAnnotations() ?? editingSnapshot.annotations;
        const dirty =
            editVariant !== editingSnapshot.baseVariant ||
            !annotationsEqual(latest, editingSnapshot.annotations);
        if (dirty) {
            setConfirmDiscardOpen(true);
        } else {
            stopEditing();
        }
    };

    const handleEditSave = () => {
        if (!editingSnapshot || !workspaceRef.current) return;
        const { annotations, compositedDataUrl } = workspaceRef.current.bake();
        updateSnapshotAnnotations(editingSnapshot.id, annotations, compositedDataUrl, editVariant);
        stopEditing();
    };

    // ── Edit mode: annotate a saved snapshot in the same window ──
    if (editingSnapshot) {
        const editImageUrl =
            editVariant === 'overlay'
                ? editingSnapshot.dataUrl
                : (editingSnapshot.dataUrlNoOverlay ?? editingSnapshot.dataUrl);

        return (
            <SnapshotWindow
                icon={Pencil}
                title="Edit annotations"
                headerActions={
                    <>
                        <Button variant="outline" size="sm" className="text-xs" onClick={requestStopEditing}>
                            Back
                        </Button>
                        <Button
                            variant="default"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={handleEditSave}
                        >
                            <Save className="h-3.5 w-3.5" />
                            Save
                        </Button>
                        <Separator orientation="vertical" className="h-6" />
                    </>
                }
                // Escape/X leaves the edit view (back to the gallery), after a
                // discard confirmation when there are unsaved annotation changes.
                onClose={requestStopEditing}
                closeDisabled={confirmDiscardOpen}
            >
                <SnapshotAnnotationWorkspace
                    key={editingSnapshot.id}
                    ref={workspaceRef}
                    imageUrl={editImageUrl}
                    initialAnnotations={editingSnapshot.annotations}
                    sidebarExtras={
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-2 text-xs"
                            disabled={!editingSnapshot.dataUrlNoOverlay}
                            onClick={() =>
                                setEditVariant((v) => (v === 'overlay' ? 'no-overlay' : 'overlay'))
                            }
                        >
                            {editVariant === 'overlay' ? (
                                <Eye className="h-4 w-4" />
                            ) : (
                                <EyeOff className="h-4 w-4" />
                            )}
                            Overlays
                        </Button>
                    }
                />
                <ConfirmationDialog
                    open={confirmDiscardOpen}
                    onOpenChange={setConfirmDiscardOpen}
                    title="Discard annotation changes?"
                    description="Unsaved changes to this screenshot's annotations will be lost."
                    confirmLabel="Discard"
                    cancelLabel="Keep editing"
                    variant="destructive"
                    onConfirm={stopEditing}
                />
            </SnapshotWindow>
        );
    }

    // ── Gallery mode ──
    return (
        <SnapshotWindow
            icon={Camera}
            title="Screenshots"
            titleAccessory={
                snapshots.length > 0 ? (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {snapshots.length}
                    </span>
                ) : undefined
            }
            onClose={onClose}
        >
            <div className="flex min-h-0 flex-1">
                {/* Left sidebar */}
                <div className="flex w-36 shrink-0 flex-col border-r border-border sm:w-52">
                    <div className="flex-1 overflow-y-auto p-2">
                        {snapshots.length === 0 ? (
                            <p className="mt-4 text-center text-xs text-muted-foreground">No screenshots</p>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {snapshots.map((snapshot) => (
                                    <SnapshotThumbnail
                                        key={snapshot.id}
                                        snapshot={snapshot}
                                        isSelected={snapshot.id === selectedSnapshotId}
                                        onSelect={() => setSelectedSnapshotId(snapshot.id)}
                                        onRemove={
                                            snapshot.origin === 'user'
                                                ? () => handleRemove(snapshot.id)
                                                : undefined
                                        }
                                        onToggleReport={() => toggleReportInclusion(snapshot.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Main area: preview + label editor */}
                <div className="flex min-h-0 flex-1 flex-col">
                    {/* Image preview */}
                    <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-muted/30 p-3">
                        {selectedSnapshot ? (
                            <>
                                <img
                                    src={getSnapshotDisplayUrl(selectedSnapshot)}
                                    alt={
                                        selectedSnapshot.label ?? VIEW_MODE_LABELS[selectedSnapshot.viewMode]
                                    }
                                    className="h-full w-auto max-w-full rounded object-contain shadow-md"
                                />
                                <div className="absolute right-3 top-3 flex items-center gap-1.5">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="gap-1.5 text-xs shadow-md"
                                        disabled={!selectedSnapshot.dataUrlNoOverlay || isRebaking}
                                        onClick={() => handleToggleOverlay(selectedSnapshot)}
                                    >
                                        {selectedSnapshot.baseVariant === 'overlay' ? (
                                            <Eye className="h-3.5 w-3.5" />
                                        ) : (
                                            <EyeOff className="h-3.5 w-3.5" />
                                        )}
                                        Overlays
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="gap-1.5 text-xs shadow-md"
                                        disabled={isRebaking}
                                        onClick={() => startEditing(selectedSnapshot)}
                                    >
                                        <Pencil className="h-3.5 w-3.5" />
                                        Annotate
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <Camera className="h-10 w-10 opacity-20" />
                                <p className="text-sm">No screenshots yet</p>
                                <p className="text-xs">
                                    Use the camera button in a viewport toolbar to capture the current view
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Description editor */}
                    {selectedSnapshot && (
                        <div className="shrink-0 border-t border-border px-3 py-2">
                            <div className="flex items-center justify-between pb-1">
                                <span className="text-xs font-medium text-muted-foreground">Description</span>
                                <span className="text-xs text-muted-foreground">
                                    {editingDescription.length}/{SNAPSHOT_DESCRIPTION_MAX_LENGTH}
                                </span>
                            </div>
                            <textarea
                                value={editingDescription}
                                onChange={(e) => setEditingDescription(e.target.value)}
                                onBlur={handleDescriptionBlur}
                                maxLength={SNAPSHOT_DESCRIPTION_MAX_LENGTH}
                                placeholder="Add a description to this snapshot…"
                                rows={2}
                                className="w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    )}
                </div>
            </div>
        </SnapshotWindow>
    );
}

// ============================================================================
// SnapshotDraftOverlay (global capture flow)
// ============================================================================

/**
 * Renders the capture wizard for a freshly captured draft.
 *
 * Mount this once in the viewer shell so capturing a screenshot from any
 * viewport (in any workflow step) surfaces the wizard — independently of
 * where {@link SnapshotPanelButton} lives.
 */
export function SnapshotDraftOverlay() {
    const { draftSnapshot, dismissDraft, saveDraft } = useViewportSnapshot();

    if (!draftSnapshot) return null;

    return (
        <SnapshotCaptureWizard
            snapshot={draftSnapshot}
            dataUrlWithOverlay={draftSnapshot.dataUrl}
            dataUrlNoOverlay={draftSnapshot.dataUrlNoOverlay ?? draftSnapshot.dataUrl}
            onClose={dismissDraft}
            onSave={(useOverlayImage, description) =>
                saveDraft({
                    baseVariant: useOverlayImage ? 'overlay' : 'no-overlay',
                    description: description.trim() || undefined,
                })
            }
            onSaveAnnotated={(annotations, annotatedDataUrl, baseVariant, description) =>
                saveDraft({
                    baseVariant,
                    annotations,
                    annotatedDataUrl: annotatedDataUrl ?? undefined,
                    description: description.trim() || undefined,
                })
            }
        />
    );
}

// ============================================================================
// SnapshotPanelButton (public export)
// ============================================================================

export interface SnapshotPanelButtonProps {
    /** Optional extra classes merged onto the trigger button (e.g. `w-full`). */
    className?: string;
}

export function SnapshotPanelButton({ className }: SnapshotPanelButtonProps = {}) {
    const [panelOpen, setPanelOpen] = useState(false);

    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant={panelOpen ? 'secondary' : 'outline'}
                        size="sm"
                        className={cn('gap-2', className)}
                        onClick={() => setPanelOpen((v) => !v)}
                    >
                        <Camera className="h-4 w-4" />
                        <span>Screenshots</span>
                    </Button>
                </TooltipTrigger>
                <TooltipContent>Screen captures</TooltipContent>
            </Tooltip>

            {panelOpen && <SnapshotPanel onClose={() => setPanelOpen(false)} />}
        </>
    );
}
